import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../../lib/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import PrimesTab from './PrimesTab';

const FinancierTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('commissions');

  const subTabs = [
    { id: 'commissions', label: '💰 Commissions' },
    { id: 'reconciliation', label: '📋 Réconciliation' },
    { id: 'comptabilite', label: '📊 Comptabilité' },
    { id: 'rapports', label: '📄 Rapports' },
    { id: 'ecritures', label: '✍️ Saisie Écritures' },
    { id: 'primes', label: '🎁 Primes' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">💼 Module Financier - Comptabilité SYSCOHADA</h2>
      </div>

      {/* Sous-onglets */}
      <div className="flex space-x-2 border-b border-gray-200">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeSubTab === tab.id
                ? 'text-violet-600 border-b-2 border-violet-600 bg-violet-50'
                : 'text-gray-600 hover:text-violet-600 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      <div>
        {activeSubTab === 'commissions' && <CommissionsTab />}
        {activeSubTab === 'reconciliation' && <ReconciliationTab />}
        {activeSubTab === 'comptabilite' && <ComptabiliteTab />}
        {activeSubTab === 'rapports' && <RapportsTab />}
        {activeSubTab === 'ecritures' && <EcrituresTab />}
        {activeSubTab === 'primes' && <PrimesTab />}
      </div>
    </div>
  );
};

// SOUS-ONGLET 1 : COMMISSIONS
const CommissionsTab = () => {
  const [resume, setResume] = useState(null);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('mois');

  useEffect(() => {
    loadData();
  }, [periode]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resumeRes, entreprisesRes] = await Promise.all([
        api.get(`/comptabilite/commissions/resume?periode=${periode}`),
        api.get(`/comptabilite/commissions/par-entreprise?periode=${periode}`)
      ]);
      setResume(resumeRes.data);
      setEntreprises(entreprisesRes.data.entreprises);
    } catch (error) {
      console.error('Erreur chargement commissions:', error);
      toast.error('Erreur lors du chargement des commissions');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `${Math.round(amount || 0).toLocaleString('fr-FR')} GNF`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  // Données pour graphique
  const chartData = entreprises.slice(0, 10).map(ent => ({
    name: ent.entreprise_nom.substring(0, 20),
    commissions: ent.total_commissions
  }));

  return (
    <div className="space-y-6">
      {/* Filtres */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <label className="font-medium text-gray-700">Période :</label>
          <select 
            value={periode} 
            onChange={(e) => setPeriode(e.target.value)} 
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="jour">Aujourd'hui</option>
            <option value="semaine">Cette semaine</option>
            <option value="mois">Ce mois</option>
            <option value="trimestre">Ce trimestre</option>
            <option value="annee">Cette année</option>
          </select>
        </div>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <span>🖨️</span>
          <span>Imprimer</span>
        </button>
      </div>

      {/* KPIs */}
      {resume && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-6">
              <p className="text-sm text-blue-600 font-medium mb-1">Total Avances</p>
              <p className="text-3xl font-bold text-blue-900">{formatCurrency(resume.total_avances)}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-6">
              <p className="text-sm text-purple-600 font-medium mb-1">Nombre d'Avances</p>
              <p className="text-3xl font-bold text-purple-900">{resume.nb_avances}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-6">
              <p className="text-sm text-green-600 font-medium mb-1">Total Commissions</p>
              <p className="text-3xl font-bold text-green-900">{formatCurrency(resume.total_commissions)}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
            <CardContent className="p-6">
              <p className="text-sm text-orange-600 font-medium mb-1">Taux Moyen</p>
              <p className="text-3xl font-bold text-orange-900">{resume.taux_moyen}%</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Graphique */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>📊 Top 10 Entreprises par Commissions</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="commissions" fill="#7C3AED" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Tableau détaillé */}
      <Card>
        <CardHeader>
          <CardTitle>📋 Détail par Entreprise</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entreprise
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taux
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nb Avances
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Avances
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commissions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entreprises.map((ent) => (
                  <tr key={ent.entreprise_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {ent.entreprise_nom}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        {ent.taux_commission}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {ent.nb_avances}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(ent.total_avances)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                      {formatCurrency(ent.total_commissions)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// SOUS-ONGLET 2 : RÉCONCILIATION
const ReconciliationTab = () => {
  const [reconciliations, setReconciliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalValidation, setModalValidation] = useState({ open: false, reconciliation: null });

  useEffect(() => {
    loadReconciliations();
  }, []);

  const loadReconciliations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/comptabilite/reconciliation/en-attente');
      setReconciliations(response.data.reconciliations);
    } catch (error) {
      console.error('Erreur chargement réconciliations:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleValider = async (id, montant, commentaire) => {
    try {
      await api.post(`/comptabilite/reconciliation/${id}/valider`, { 
        montant_valide: montant,
        commentaire 
      });
      toast.success('✅ Rechargement validé et solde crédité');
      setModalValidation({ open: false, reconciliation: null });
      loadReconciliations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleRejeter = async (id) => {
    const motif = window.prompt('Motif du rejet :');
    if (!motif) return;
    
    try {
      await api.post(`/comptabilite/reconciliation/${id}/rejeter`, { motif });
      toast.success('❌ Rechargement rejeté');
      loadReconciliations();
    } catch (error) {
      toast.error('Erreur lors du rejet');
    }
  };

  const formatCurrency = (amount) => {
    return `${Math.round(amount || 0).toLocaleString('fr-FR')} GNF`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold">📋 Rechargements en attente de validation</h3>
        <div className="px-4 py-2 bg-orange-100 text-orange-800 rounded-lg font-medium">
          {reconciliations.length} en attente
        </div>
      </div>

      {reconciliations.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <p className="text-6xl mb-4">✅</p>
            <p className="text-xl text-gray-700 font-medium">Aucun rechargement en attente</p>
            <p className="text-gray-500 mt-2">Tous les rechargements ont été traités</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {reconciliations.map((rec) => (
            <Card key={rec.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h4 className="text-lg font-bold text-gray-900">{rec.entreprise_nom}</h4>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                        EN ATTENTE
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Montant déclaré</p>
                        <p className="text-lg font-bold text-gray-900">{formatCurrency(rec.montant)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Date opération</p>
                        <p className="text-sm font-medium text-gray-700">
                          {new Date(rec.date_operation).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Référence</p>
                        <p className="text-sm font-mono text-gray-700">{rec.reference || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2 ml-6">
                    <button
                      onClick={() => setModalValidation({ open: true, reconciliation: rec })}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center space-x-2"
                    >
                      <span>✅</span>
                      <span>Valider</span>
                    </button>
                    <button
                      onClick={() => handleRejeter(rec.id)}
                      className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center justify-center space-x-2"
                    >
                      <span>❌</span>
                      <span>Rejeter</span>
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal Validation */}
      {modalValidation.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">✅ Valider le rechargement</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Entreprise</p>
                <p className="font-bold text-lg">{modalValidation.reconciliation.entreprise_nom}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant à créditer
                </label>
                <input
                  type="number"
                  id="montant-validation"
                  defaultValue={modalValidation.reconciliation.montant}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commentaire (optionnel)
                </label>
                <textarea
                  id="commentaire-validation"
                  rows="3"
                  placeholder="Notes internes..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  const montant = parseFloat(document.getElementById('montant-validation').value);
                  const commentaire = document.getElementById('commentaire-validation').value;
                  handleValider(modalValidation.reconciliation.id, montant, commentaire);
                }}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                ✅ Confirmer et Créditer
              </button>
              <button
                onClick={() => setModalValidation({ open: false, reconciliation: null })}
                className="flex-1 px-4 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// SOUS-ONGLET 3 : COMPTABILITÉ
const ComptabiliteTab = () => {
  const [bilan, setBilan] = useState(null);
  const [compteResultat, setCompteResultat] = useState(null);
  const [tresorerie, setTresorerie] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComptabilite();
  }, []);

  const loadComptabilite = async () => {
    try {
      setLoading(true);
      const [bilanRes, crRes, tresRes] = await Promise.all([
        api.get('/comptabilite/bilan'),
        api.get('/comptabilite/compte-resultat'),
        api.get('/comptabilite/tresorerie')
      ]);
      setBilan(bilanRes.data);
      setCompteResultat(crRes.data);
      setTresorerie(tresRes.data);
    } catch (error) {
      console.error('Erreur chargement comptabilité:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `${Math.round(amount || 0).toLocaleString('fr-FR')} GNF`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bilan Comptable */}
      {bilan && (
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
            <CardTitle className="text-white">📊 Bilan Comptable SYSCOHADA</CardTitle>
            <p className="text-blue-100 text-sm">Au {bilan.date_bilan} - Exercice {bilan.exercice}</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-8">
              {/* ACTIF */}
              <div className="space-y-4">
                <h4 className="text-xl font-bold text-blue-600 border-b-2 border-blue-600 pb-2">
                  ACTIF
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                    <span className="text-gray-700">Actif immobilisé</span>
                    <span className="font-semibold">{formatCurrency(bilan.actif_immobilise)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                    <span className="text-gray-700">Actif circulant</span>
                    <span className="font-semibold">{formatCurrency(bilan.actif_circulant)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                    <span className="text-gray-700">Trésorerie</span>
                    <span className="font-semibold">{formatCurrency(bilan.tresorerie_actif)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-blue-600 text-white rounded-lg">
                    <span className="font-bold text-lg">TOTAL ACTIF</span>
                    <span className="font-bold text-2xl">{formatCurrency(bilan.total_actif)}</span>
                  </div>
                </div>
              </div>

              {/* PASSIF */}
              <div className="space-y-4">
                <h4 className="text-xl font-bold text-green-600 border-b-2 border-green-600 pb-2">
                  PASSIF
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                    <span className="text-gray-700">Capitaux propres</span>
                    <span className="font-semibold">{formatCurrency(bilan.capitaux_propres)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                    <span className="text-gray-700">Dettes</span>
                    <span className="font-semibold">{formatCurrency(bilan.dettes)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                    <span className="text-gray-700">Trésorerie</span>
                    <span className="font-semibold">{formatCurrency(bilan.tresorerie_passif)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-green-600 text-white rounded-lg">
                    <span className="font-bold text-lg">TOTAL PASSIF</span>
                    <span className="font-bold text-2xl">{formatCurrency(bilan.total_passif)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Compte de Résultat */}
      {compteResultat && (
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-800 text-white">
            <CardTitle className="text-white">💹 Compte de Résultat</CardTitle>
            <p className="text-purple-100 text-sm">Du {compteResultat.date_debut} au {compteResultat.date_fin}</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-8">
              {/* PRODUITS */}
              <div className="space-y-4">
                <h4 className="text-xl font-bold text-green-600 border-b-2 border-green-600 pb-2">
                  PRODUITS
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                    <span className="text-gray-700">Produits d'exploitation</span>
                    <span className="font-semibold">{formatCurrency(compteResultat.produits_exploitation)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-green-600 text-white rounded-lg">
                    <span className="font-bold text-lg">TOTAL PRODUITS</span>
                    <span className="font-bold text-2xl">{formatCurrency(compteResultat.total_produits)}</span>
                  </div>
                </div>
              </div>

              {/* CHARGES */}
              <div className="space-y-4">
                <h4 className="text-xl font-bold text-red-600 border-b-2 border-red-600 pb-2">
                  CHARGES
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                    <span className="text-gray-700">Charges d'exploitation</span>
                    <span className="font-semibold">{formatCurrency(compteResultat.charges_exploitation)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-red-600 text-white rounded-lg">
                    <span className="font-bold text-lg">TOTAL CHARGES</span>
                    <span className="font-bold text-2xl">{formatCurrency(compteResultat.total_charges)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* RÉSULTAT NET */}
            <div className="mt-8 p-6 bg-gradient-to-r from-violet-100 to-violet-200 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold text-gray-900">RÉSULTAT NET</span>
                <span className={`text-4xl font-bold ${
                  compteResultat.resultat_net >= 0 ? 'text-green-700' : 'text-red-700'
                }`}>
                  {compteResultat.resultat_net >= 0 ? '+' : ''}{formatCurrency(compteResultat.resultat_net)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trésorerie */}
      {tresorerie && (
        <Card>
          <CardHeader className="bg-gradient-to-r from-orange-600 to-orange-800 text-white">
            <CardTitle className="text-white">💰 État de la Trésorerie</CardTitle>
            <p className="text-orange-100 text-sm">Du {tresorerie.date_debut} au {tresorerie.date_fin}</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="p-6 bg-green-50 rounded-lg border-l-4 border-green-600">
                  <p className="text-sm text-gray-600 mb-1">Entrées (Rechargements)</p>
                  <p className="text-3xl font-bold text-green-700">+ {formatCurrency(tresorerie.total_entrees)}</p>
                </div>
                <div className="p-6 bg-red-50 rounded-lg border-l-4 border-red-600">
                  <p className="text-sm text-gray-600 mb-1">Sorties (Avances décaissées)</p>
                  <p className="text-3xl font-bold text-red-700">- {formatCurrency(tresorerie.total_sorties)}</p>
                </div>
              </div>

              <div className="p-6 bg-blue-50 rounded-lg border-l-4 border-blue-600">
                <p className="text-sm text-gray-600 mb-1">Solde net période</p>
                <p className={`text-3xl font-bold ${
                  tresorerie.solde_net >= 0 ? 'text-blue-700' : 'text-red-700'
                }`}>
                  {tresorerie.solde_net >= 0 ? '+' : ''}{formatCurrency(tresorerie.solde_net)}
                </p>
              </div>

              <div className="p-6 bg-gradient-to-r from-violet-600 to-violet-800 text-white rounded-lg">
                <p className="text-violet-100 mb-2">Solde actuel total (toutes entreprises)</p>
                <p className="text-4xl font-bold">{formatCurrency(tresorerie.solde_actuel)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// SOUS-ONGLET 4 : RAPPORTS
const RapportsTab = () => {
  const [journal, setJournal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  useEffect(() => {
    // Dates par défaut (mois en cours)
    const now = new Date();
    const debut = new Date(now.getFullYear(), now.getMonth(), 1);
    setDateDebut(debut.toISOString().split('T')[0]);
    setDateFin(now.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (dateDebut && dateFin) {
      loadJournal();
    }
  }, [dateDebut, dateFin]);

  const loadJournal = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/comptabilite/rapports/journal?date_debut=${dateDebut}&date_fin=${dateFin}`);
      setJournal(response.data.operations);
    } catch (error) {
      console.error('Erreur chargement journal:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `${Math.round(amount || 0).toLocaleString('fr-FR')} GNF`;
  };

  const handleExportCSV = () => {
    const headers = ['Date', 'Type', 'Entreprise', 'Employé', 'Description', 'Montant', 'Commission'];
    const rows = journal.map(op => [
      new Date(op.date).toLocaleDateString('fr-FR'),
      op.type,
      op.entreprise,
      op.employe,
      op.description,
      op.montant,
      op.commission
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `journal_operations_${dateDebut}_${dateFin}.csv`;
    link.click();

    toast.success('✅ Export CSV réussi');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtres et Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <span>📥</span>
            <span>Export Excel</span>
          </button>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <span>🖨️</span>
            <span>Imprimer</span>
          </button>
        </div>
      </div>

      {/* Journal des Opérations */}
      <Card>
        <CardHeader>
          <CardTitle>📑 Journal des Opérations Comptables</CardTitle>
          <p className="text-sm text-gray-600">{journal.length} opérations trouvées</p>
        </CardHeader>
        <CardContent>
          {journal.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-4xl mb-3">📭</p>
              <p>Aucune opération sur cette période</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entreprise
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Montant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {journal.map((op, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {new Date(op.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {op.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {op.entreprise}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {op.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(op.montant)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                        {formatCurrency(op.commission)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// SOUS-ONGLET 5 : SAISIE ÉCRITURES
const EcrituresTab = () => {
  const [ecritures, setEcritures] = useState([]);
  const [modeles, setModeles] = useState({});
  const [planComptable, setPlanComptable] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalSaisie, setModalSaisie] = useState({ open: false });
  const [modalEdit, setModalEdit] = useState({ open: false, ecriture: null });
  const { user } = useAuth();
  
  // Filtres
  const [filtres, setFiltres] = useState({
    dateDebut: '',
    dateFin: '',
    statut: '',
    journal: '',
    recherche: ''
  });
  
  // Balances
  const [balances, setBalances] = useState({
    totalDebit: 0,
    totalCredit: 0,
    solde: 0,
    nbEcritures: 0
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0
  });
  
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    // Initialiser dates par défaut (mois en cours)
    const now = new Date();
    const debut = new Date(now.getFullYear(), now.getMonth(), 1);
    setFiltres(prev => ({
      ...prev,
      dateDebut: debut.toISOString().split('T')[0],
      dateFin: now.toISOString().split('T')[0]
    }));
  }, []);

  useEffect(() => {
    if (filtres.dateDebut && filtres.dateFin) {
      loadData();
    }
  }, [filtres, pagination.skip]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Construire params
      const params = new URLSearchParams({
        skip: pagination.skip.toString(),
        limit: pagination.limit.toString()
      });
      
      if (filtres.dateDebut) params.append('date_debut', filtres.dateDebut);
      if (filtres.dateFin) params.append('date_fin', filtres.dateFin);
      if (filtres.statut) params.append('statut', filtres.statut);
      
      const [ecrituresRes, modelesRes, planRes] = await Promise.all([
        api.get(`/comptabilite/ecritures?${params.toString()}`),
        api.get('/comptabilite/ecritures/modeles'),
        api.get('/comptabilite/plan-comptable')
      ]);
      
      const data = ecrituresRes.data;
      let filteredEcritures = data.ecritures || [];
      
      // Filtrage local pour journal et recherche
      if (filtres.journal) {
        filteredEcritures = filteredEcritures.filter(e => e.journal === filtres.journal);
      }
      
      if (filtres.recherche) {
        const terme = filtres.recherche.toLowerCase();
        filteredEcritures = filteredEcritures.filter(e => 
          e.libelle.toLowerCase().includes(terme) ||
          e.numero_piece.toLowerCase().includes(terme) ||
          e.lignes.some(l => l.compte.includes(terme) || l.libelle.toLowerCase().includes(terme))
        );
      }
      
      setEcritures(filteredEcritures);
      setModeles(modelesRes.data.modeles);
      setPlanComptable(planRes.data.plan_comptable);
      setPagination(prev => ({ ...prev, total: data.total || 0 }));
      
      // Calculer balances
      const totalDebit = filteredEcritures.reduce((sum, e) => sum + (e.montant_total || 0), 0);
      const totalCredit = totalDebit; // Toujours équilibré
      setBalances({
        totalDebit,
        totalCredit,
        solde: 0, // Toujours 0 car équilibré
        nbEcritures: filteredEcritures.length
      });
      
    } catch (error) {
      console.error('Erreur chargement écritures:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmer la suppression de cette écriture ?')) return;
    
    try {
      await api.delete(`/comptabilite/ecritures/${id}`);
      toast.success('✅ Écriture supprimée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  const handleResetFiltres = () => {
    const now = new Date();
    const debut = new Date(now.getFullYear(), now.getMonth(), 1);
    setFiltres({
      dateDebut: debut.toISOString().split('T')[0],
      dateFin: now.toISOString().split('T')[0],
      statut: '',
      journal: '',
      recherche: ''
    });
    setPagination(prev => ({ ...prev, skip: 0 }));
  };
  
  const handlePageChange = (direction) => {
    if (direction === 'next' && pagination.skip + pagination.limit < pagination.total) {
      setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }));
    } else if (direction === 'prev' && pagination.skip > 0) {
      setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }));
    }
  };

  const handleExportExcel = () => {
    const headers = ['Date', 'N° Pièce', 'Journal', 'Libellé', 'Compte', 'Débit', 'Crédit'];
    const rows = [];
    
    ecritures.forEach(ec => {
      ec.lignes.forEach((ligne, idx) => {
        rows.push([
          idx === 0 ? new Date(ec.date_ecriture).toLocaleDateString('fr-FR') : '',
          idx === 0 ? ec.numero_piece : '',
          idx === 0 ? ec.journal : '',
          ligne.libelle,
          ligne.compte,
          ligne.debit || 0,
          ligne.credit || 0
        ]);
      });
      rows.push(['', '', '', '', 'Total', ec.montant_total, ec.montant_total]);
      rows.push(['']); // Ligne vide
    });

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ecritures_comptables_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('✅ Export réussi');
  };

  const formatCurrency = (amount) => {
    return `${Math.round(amount || 0).toLocaleString('fr-FR')} GNF`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold">✍️ Écritures Comptables Manuelles</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setModalSaisie({ open: true })}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Nouvelle Écriture</span>
          </button>
          <button
            onClick={handleExportExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <span>📥</span>
            <span>Export Excel</span>
          </button>
        </div>
      </div>

      {/* Section Balances */}
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50">
        <CardContent className="p-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4">📊 Balances de la période</h4>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500">
              <p className="text-sm text-gray-600 mb-1">Nombre d'écritures</p>
              <p className="text-3xl font-bold text-blue-700">{balances.nbEcritures}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-l-4 border-green-500">
              <p className="text-sm text-gray-600 mb-1">Total Débit</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(balances.totalDebit)}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-l-4 border-red-500">
              <p className="text-sm text-gray-600 mb-1">Total Crédit</p>
              <p className="text-2xl font-bold text-red-700">{formatCurrency(balances.totalCredit)}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-l-4 border-purple-500">
              <p className="text-sm text-gray-600 mb-1">Solde</p>
              <p className={`text-2xl font-bold ${balances.solde === 0 ? 'text-purple-700' : 'text-orange-700'}`}>
                {formatCurrency(balances.solde)}
              </p>
              {balances.solde === 0 && <p className="text-xs text-green-600 mt-1">✅ Équilibré</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtres */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-800">🔍 Filtres</h4>
            <button
              onClick={handleResetFiltres}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              ↻ Réinitialiser
            </button>
          </div>
          
          <div className="grid grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
              <input
                type="date"
                value={filtres.dateDebut}
                onChange={(e) => setFiltres(prev => ({ ...prev, dateDebut: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
              <input
                type="date"
                value={filtres.dateFin}
                onChange={(e) => setFiltres(prev => ({ ...prev, dateFin: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={filtres.statut}
                onChange={(e) => setFiltres(prev => ({ ...prev, statut: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">Tous</option>
                <option value="BROUILLON">BROUILLON</option>
                <option value="VALIDEE">VALIDÉE</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Journal</label>
              <select
                value={filtres.journal}
                onChange={(e) => setFiltres(prev => ({ ...prev, journal: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">Tous</option>
                <option value="VENTES">VENTES</option>
                <option value="ACHATS">ACHATS</option>
                <option value="BANQUE">BANQUE</option>
                <option value="CAISSE">CAISSE</option>
                <option value="OPERATIONS_DIVERSES">OPÉRATIONS DIVERSES</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
              <input
                type="text"
                value={filtres.recherche}
                onChange={(e) => setFiltres(prev => ({ ...prev, recherche: e.target.value }))}
                placeholder="N° pièce, libellé, compte..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des écritures */}
      {ecritures.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <p className="text-6xl mb-4">📝</p>
            <p className="text-xl text-gray-700 font-medium">Aucune écriture trouvée</p>
            <p className="text-gray-500 mt-2">
              {filtres.recherche || filtres.statut || filtres.journal 
                ? 'Essayez de modifier vos filtres' 
                : 'Cliquez sur "Nouvelle Écriture" pour commencer'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {ecritures.map((ecriture) => (
              <Card key={ecriture.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-bold">{ecriture.numero_piece}</h4>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {ecriture.journal}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          ecriture.statut === 'VALIDEE' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {ecriture.statut}
                        </span>
                      </div>
                      <p className="text-gray-700 font-medium">{ecriture.libelle}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(ecriture.date_ecriture).toLocaleDateString('fr-FR')} • 
                        Créé par {ecriture.created_by_email || 'N/A'}
                      </p>
                    </div>
                    
                    {isSuperAdmin && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setModalEdit({ open: true, ecriture })}
                          className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                        >
                          ✏️ Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(ecriture.id)}
                          className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                        >
                          🗑️ Supprimer
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Lignes d'écriture */}
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[480px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Compte</th>
                        <th className="px-4 py-2 text-left">Libellé</th>
                        <th className="px-4 py-2 text-right">Débit</th>
                        <th className="px-4 py-2 text-right">Crédit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ecriture.lignes.map((ligne, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="px-4 py-2 font-mono text-blue-600">{ligne.compte}</td>
                          <td className="px-4 py-2">{ligne.libelle}</td>
                          <td className="px-4 py-2 text-right font-medium">
                            {ligne.debit > 0 ? formatCurrency(ligne.debit) : '-'}
                          </td>
                          <td className="px-4 py-2 text-right font-medium">
                            {ligne.credit > 0 ? formatCurrency(ligne.credit) : '-'}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-bold">
                        <td colSpan="2" className="px-4 py-2">TOTAL</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(ecriture.montant_total)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(ecriture.montant_total)}</td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600">
                Affichage de {pagination.skip + 1} à {Math.min(pagination.skip + pagination.limit, pagination.total)} sur {pagination.total} écritures
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange('prev')}
                  disabled={pagination.skip === 0}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Précédent
                </button>
                <button
                  onClick={() => handlePageChange('next')}
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal Saisie */}
      {modalSaisie.open && (
        <ModalSaisieEcriture
          modeles={modeles}
          planComptable={planComptable}
          onClose={() => setModalSaisie({ open: false })}
          onSuccess={() => { setModalSaisie({ open: false }); loadData(); }}
        />
      )}

      {/* Modal Edition (Super Admin uniquement) */}
      {modalEdit.open && isSuperAdmin && (
        <ModalEditEcriture
          ecriture={modalEdit.ecriture}
          planComptable={planComptable}
          onClose={() => setModalEdit({ open: false, ecriture: null })}
          onSuccess={() => { setModalEdit({ open: false, ecriture: null }); loadData(); }}
        />
      )}
    </div>
  );
};

// Modal Saisie Nouvelle Écriture
const ModalSaisieEcriture = ({ modeles, planComptable, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    date_ecriture: new Date().toISOString().split('T')[0],
    journal: 'OPERATIONS_DIVERSES',
    libelle: '',
    lignes: [
      { compte: '', libelle: '', debit: 0, credit: 0 },
      { compte: '', libelle: '', debit: 0, credit: 0 }
    ]
  });

  const handleModeleSelect = (modeleKey) => {
    const modele = modeles[modeleKey];
    setFormData({
      ...formData,
      libelle: modele.libelle,
      journal: modele.journal,
      lignes: JSON.parse(JSON.stringify(modele.lignes)) // Clone profond
    });
  };

  const handleAddLigne = () => {
    setFormData({
      ...formData,
      lignes: [...formData.lignes, { compte: '', libelle: '', debit: 0, credit: 0 }]
    });
  };

  const handleRemoveLigne = (index) => {
    if (formData.lignes.length <= 2) {
      toast.warning('Minimum 2 lignes requises');
      return;
    }
    const newLignes = formData.lignes.filter((_, i) => i !== index);
    setFormData({ ...formData, lignes: newLignes });
  };

  const handleLigneChange = (index, field, value) => {
    const newLignes = [...formData.lignes];
    newLignes[index][field] = field === 'debit' || field === 'credit' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, lignes: newLignes });
  };

  const calculateTotals = () => {
    const totalDebit = formData.lignes.reduce((sum, l) => sum + (l.debit || 0), 0);
    const totalCredit = formData.lignes.reduce((sum, l) => sum + (l.credit || 0), 0);
    return { totalDebit, totalCredit, balanced: Math.abs(totalDebit - totalCredit) < 0.01 };
  };

  const handleSubmit = async () => {
    const { totalDebit, totalCredit, balanced } = calculateTotals();
    
    if (!balanced) {
      toast.error(`❌ Écriture déséquilibrée ! Débit: ${totalDebit} ≠ Crédit: ${totalCredit}`);
      return;
    }

    if (!formData.libelle) {
      toast.error('Le libellé est obligatoire');
      return;
    }

    try {
      await api.post('/comptabilite/ecritures', formData);
      toast.success('✅ Écriture enregistrée avec succès');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la saisie');
    }
  };

  const { totalDebit, totalCredit, balanced } = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full my-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900">✍️ Nouvelle Écriture Comptable</h3>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Modèles prédéfinis */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Modèle (optionnel)</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(modeles).map(([key, modele]) => (
                <button
                  key={key}
                  onClick={() => handleModeleSelect(key)}
                  className="px-3 py-2 border border-violet-300 text-violet-700 rounded-lg hover:bg-violet-50 text-sm"
                >
                  {modele.libelle}
                </button>
              ))}
            </div>
          </div>

          {/* En-tête */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={formData.date_ecriture}
                onChange={(e) => setFormData({ ...formData, date_ecriture: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Journal</label>
              <select
                value={formData.journal}
                onChange={(e) => setFormData({ ...formData, journal: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="VENTES">VENTES</option>
                <option value="ACHATS">ACHATS</option>
                <option value="BANQUE">BANQUE</option>
                <option value="CAISSE">CAISSE</option>
                <option value="OPERATIONS_DIVERSES">OPÉRATIONS DIVERSES</option>
              </select>
            </div>
            <div className="col-span-1"></div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Libellé *</label>
            <input
              type="text"
              value={formData.libelle}
              onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
              placeholder="Ex: Paiement salaires du mois..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* Lignes */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Lignes d'écriture</label>
              <button
                onClick={handleAddLigne}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              >
                + Ajouter ligne
              </button>
            </div>

            <div className="space-y-3">
              {formData.lignes.map((ligne, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-start p-3 bg-gray-50 rounded-lg">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={ligne.compte}
                      onChange={(e) => handleLigneChange(index, 'compte', e.target.value)}
                      placeholder="Compte"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
                    />
                  </div>
                  <div className="col-span-4">
                    <input
                      type="text"
                      value={ligne.libelle}
                      onChange={(e) => handleLigneChange(index, 'libelle', e.target.value)}
                      placeholder="Libellé de la ligne"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={ligne.debit || ''}
                      onChange={(e) => handleLigneChange(index, 'debit', e.target.value)}
                      placeholder="Débit"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={ligne.credit || ''}
                      onChange={(e) => handleLigneChange(index, 'credit', e.target.value)}
                      placeholder="Crédit"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                    />
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <button
                      onClick={() => handleRemoveLigne(index)}
                      disabled={formData.lignes.length <= 2}
                      className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Totaux */}
            <div className={`mt-4 p-4 rounded-lg ${balanced ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex justify-between items-center text-sm font-medium">
                <span>Total Débit: {totalDebit.toFixed(2)} GNF</span>
                <span>Total Crédit: {totalCredit.toFixed(2)} GNF</span>
                <span className={balanced ? 'text-green-700' : 'text-red-700'}>
                  {balanced ? '✅ Équilibré' : '❌ Déséquilibré'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!balanced}
            className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ✅ Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal Edit (simplifié)
const ModalEditEcriture = ({ ecriture, planComptable, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    date_ecriture: ecriture.date_ecriture,
    journal: ecriture.journal,
    libelle: ecriture.libelle,
    lignes: JSON.parse(JSON.stringify(ecriture.lignes)), // Clone
    reference: ecriture.reference || ''
  });

  const handleAddLigne = () => {
    setFormData({
      ...formData,
      lignes: [...formData.lignes, { compte: '', libelle: '', debit: 0, credit: 0 }]
    });
  };

  const handleRemoveLigne = (index) => {
    if (formData.lignes.length <= 2) {
      toast.warning('Minimum 2 lignes requises');
      return;
    }
    const newLignes = formData.lignes.filter((_, i) => i !== index);
    setFormData({ ...formData, lignes: newLignes });
  };

  const handleLigneChange = (index, field, value) => {
    const newLignes = [...formData.lignes];
    newLignes[index][field] = field === 'debit' || field === 'credit' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, lignes: newLignes });
  };

  const calculateTotals = () => {
    const totalDebit = formData.lignes.reduce((sum, l) => sum + (l.debit || 0), 0);
    const totalCredit = formData.lignes.reduce((sum, l) => sum + (l.credit || 0), 0);
    return { totalDebit, totalCredit, balanced: Math.abs(totalDebit - totalCredit) < 0.01 };
  };

  const handleSubmit = async () => {
    const { totalDebit, totalCredit, balanced } = calculateTotals();
    
    if (!balanced) {
      toast.error(`❌ Écriture déséquilibrée ! Débit: ${totalDebit} ≠ Crédit: ${totalCredit}`);
      return;
    }

    if (!formData.libelle) {
      toast.error('Le libellé est obligatoire');
      return;
    }

    try {
      await api.put(`/comptabilite/ecritures/${ecriture.id}`, formData);
      toast.success('✅ Écriture modifiée avec succès');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const { totalDebit, totalCredit, balanced } = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full my-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900">✏️ Modifier l'Écriture {ecriture.numero_piece}</h3>
          <p className="text-sm text-red-600 mt-2">⚠️ Réservé au Super Admin uniquement</p>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* En-tête */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={formData.date_ecriture}
                onChange={(e) => setFormData({ ...formData, date_ecriture: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Journal</label>
              <select
                value={formData.journal}
                onChange={(e) => setFormData({ ...formData, journal: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="VENTES">VENTES</option>
                <option value="ACHATS">ACHATS</option>
                <option value="BANQUE">BANQUE</option>
                <option value="CAISSE">CAISSE</option>
                <option value="OPERATIONS_DIVERSES">OPÉRATIONS DIVERSES</option>
              </select>
            </div>
            <div></div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Libellé *</label>
            <input
              type="text"
              value={formData.libelle}
              onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* Lignes */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Lignes d'écriture</label>
              <button
                onClick={handleAddLigne}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              >
                + Ajouter ligne
              </button>
            </div>

            <div className="space-y-3">
              {formData.lignes.map((ligne, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-start p-3 bg-gray-50 rounded-lg">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={ligne.compte}
                      onChange={(e) => handleLigneChange(index, 'compte', e.target.value)}
                      placeholder="Compte"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
                    />
                  </div>
                  <div className="col-span-4">
                    <input
                      type="text"
                      value={ligne.libelle}
                      onChange={(e) => handleLigneChange(index, 'libelle', e.target.value)}
                      placeholder="Libellé"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={ligne.debit || ''}
                      onChange={(e) => handleLigneChange(index, 'debit', e.target.value)}
                      placeholder="Débit"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={ligne.credit || ''}
                      onChange={(e) => handleLigneChange(index, 'credit', e.target.value)}
                      placeholder="Crédit"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                    />
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <button
                      onClick={() => handleRemoveLigne(index)}
                      disabled={formData.lignes.length <= 2}
                      className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Totaux */}
            <div className={`mt-4 p-4 rounded-lg ${balanced ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex justify-between items-center text-sm font-medium">
                <span>Total Débit: {totalDebit.toFixed(2)} GNF</span>
                <span>Total Crédit: {totalCredit.toFixed(2)} GNF</span>
                <span className={balanced ? 'text-green-700' : 'text-red-700'}>
                  {balanced ? '✅ Équilibré' : '❌ Déséquilibré'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!balanced}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            💾 Enregistrer les modifications
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinancierTab;
